import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Pill } from './Pill';

// Canonical page-size choices — every paginated list screen's selector
// reads from here, so the options offered always match what the Platform
// Settings default can actually be set to (a default of 30 is useless if
// half the screens' dropdowns don't offer 30 at all).
export const PAGE_SIZE_OPTIONS = [5, 10, 15, 20, 25, 30, 40, 50, 100];

// Synchronous starting value before the tenant's real default has loaded —
// overwritten almost immediately by useDefaultPageSize below.
export const FALLBACK_PAGE_SIZE = 25;

// The tenant's default page size never changes mid-session, so it's fetched
// at most once per page load (module-level cache/in-flight promise) rather
// than once per screen mount — every list screen previously re-fetched it
// independently and each one visibly flashed from FALLBACK_PAGE_SIZE to the
// real value a moment later. `prefetchDefaultPageSize()` lets App.js kick
// this off at boot so the cache is usually already warm by the time a user
// navigates to any paginated screen, collapsing that flash to nothing.
let cachedDefaultPageSize = null;
let inFlightFetch = null;

function resolveDefaultPageSize() {
  if (cachedDefaultPageSize !== null) return Promise.resolve(cachedDefaultPageSize);
  if (!inFlightFetch) {
    inFlightFetch = axios.get('/api/platform-settings')
      .then((res) => {
        const value = res.data.data?.default_page_size;
        cachedDefaultPageSize = PAGE_SIZE_OPTIONS.includes(value) ? value : FALLBACK_PAGE_SIZE;
        return cachedDefaultPageSize;
      })
      .catch(() => {
        cachedDefaultPageSize = FALLBACK_PAGE_SIZE;
        return cachedDefaultPageSize;
      });
  }
  return inFlightFetch;
}

export function prefetchDefaultPageSize() {
  resolveDefaultPageSize();
}

// Platform Settings calls this right after a successful save — without it,
// every list screen visited afterward (in the same browser session, no
// full reload) kept reading the value cached from before the save, since
// resolveDefaultPageSize() above only ever fetches once per page load.
export function invalidateDefaultPageSize() {
  cachedDefaultPageSize = null;
  inFlightFetch = null;
}

// Synchronous read for a `useState` lazy initializer — returns null until
// the cache is warm, so callers should still fall back to
// FALLBACK_PAGE_SIZE for their initial state.
export function getCachedDefaultPageSize() {
  return cachedDefaultPageSize;
}

// Fetches the tenant-wide default page size (once per session, via the
// cache above) and applies it via `setLimit`. There's no user-accounts
// concept yet (see pat-architecture-reports-ui-standard.md-style system
// defaults elsewhere), so this is a system-wide default, not a per-user
// preference.
export function useDefaultPageSize(setLimit) {
  useEffect(() => {
    let cancelled = false;
    resolveDefaultPageSize().then((value) => {
      if (!cancelled) setLimit(value);
    });
    return () => { cancelled = true; };
  }, [setLimit]);
}

export function pageButtonStyle(disabled) {
  return {
    padding: '2px 7px',
    background: '#dde6f2',
    border: 'none',
    borderRadius: 4,
    fontWeight: 500,
    fontSize: 13,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  };
}

// Neighbors shown either side of the current page before collapsing the
// rest behind a "…" — e.g. current=4, pages=10 → [1, '…', 2,3,4,5,6, '…', 10].
const PAGE_WINDOW = 2;

function pageNumbers(page, pages) {
  if (pages <= 1) return [1];
  const start = Math.max(2, page - PAGE_WINDOW);
  const end = Math.min(pages - 1, page + PAGE_WINDOW);
  const nums = [1];
  if (start > 2) nums.push('…');
  for (let i = start; i <= end; i++) nums.push(i);
  if (end < pages - 1) nums.push('…');
  if (pages > 1) nums.push(pages);
  return nums;
}

function navButtonStyle(disabled) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
    padding: '4px 6px',
    background: 'none',
    border: 'none',
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 14,
    color: disabled ? '#b3bbc7' : '#333',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

function pageNumberStyle(current) {
  return {
    minWidth: 26,
    padding: '3px 6px',
    background: current ? '#dde6f2' : 'none',
    border: current ? '1px solid #bfc8d6' : '1px solid transparent',
    borderRadius: 6,
    fontWeight: current ? 700 : 400,
    fontSize: 14,
    color: '#333',
    cursor: current ? 'default' : 'pointer',
  };
}

// Canonical pagination footer — every paginated list screen renders this
// instead of hand-rolling its own Prev/Next/Page-X-of-Y block. Nav cluster
// (First/Prev/numbers/Next/Last/page-size) sits above a ruler; the total-count pill
// sits on its own line below it — grouped as one footer instead of the
// total and nav being split across opposite corners of the screen.
// `pagination`: { page, pages, total }. `setPage`: the screen's own
// useState setter (accepts a value or an updater function).
// `rightContent`: optional extra control (e.g. a page-size select), rendered
// at the end of the nav row.
// `totalLabel`: the noun for the count pill, e.g. "Products" — plural,
// capitalized, no leading count (the pill adds that itself).
export function Pagination({ pagination, setPage, rightContent, totalLabel = 'Results' }) {
  const { page, pages, total } = pagination;
  const atFirst = page <= 1;
  const atLast = page >= pages;

  return (
    <div style={{ margin: '18px 24px 0 24px', position: 'sticky', bottom: 0, background: '#f6f8fa', zIndex: 2 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '10px 4px', borderBottom: '1px solid #b3bbc7' }}>
        <button type="button" onClick={() => setPage(1)} disabled={atFirst} style={navButtonStyle(atFirst)}>
          <ChevronsLeft size={16} strokeWidth={2} /> First
        </button>
        <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={atFirst} style={navButtonStyle(atFirst)}>
          <ChevronLeft size={16} strokeWidth={2} /> Prev
        </button>
        {pageNumbers(page, pages).map((n, idx) => (
          n === '…'
            ? <span key={`dots-${idx}`} style={{ padding: '0 4px', color: '#9aa5b5', fontSize: 14 }}>…</span>
            : <button key={n} type="button" onClick={() => setPage(n)} disabled={n === page} style={pageNumberStyle(n === page)}>{n}</button>
        ))}
        <button type="button" onClick={() => setPage((p) => Math.min(p + 1, pages))} disabled={atLast} style={navButtonStyle(atLast)}>
          Next <ChevronRight size={16} strokeWidth={2} />
        </button>
        <button type="button" onClick={() => setPage(pages)} disabled={atLast} style={navButtonStyle(atLast)}>
          Last <ChevronsRight size={16} strokeWidth={2} />
        </button>
        {rightContent}
      </div>
      <div style={{ padding: '10px 4px', display: 'flex', justifyContent: 'center' }}>
        <Pill color="#333" background="#eef0f3" style={{ fontSize: 13 }}>{total} {totalLabel}</Pill>
      </div>
    </div>
  );
}

// For a list already held whole in the browser: pages `rows` and goes back to
// page 1 whenever `resetKey` (its tab or search) or the page size changes.
// Render its result with LocalPagination below.
export function useLocalPagination(rows, resetKey) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => getCachedDefaultPageSize() || FALLBACK_PAGE_SIZE);
  useDefaultPageSize(setLimit);
  useEffect(() => { setPage(1); }, [resetKey, limit]);
  const pages = Math.max(1, Math.ceil(rows.length / limit));
  const current = Math.min(page, pages);
  return {
    pageRows: rows.slice((current - 1) * limit, current * limit),
    pagination: { page: current, pages, total: rows.length },
    setPage,
    limit,
    setLimit,
  };
}

export function LocalPagination({ paging, totalLabel }) {
  return (
    <Pagination
      pagination={paging.pagination}
      setPage={paging.setPage}
      totalLabel={totalLabel}
      rightContent={(
        <select value={paging.limit} onChange={(e) => paging.setLimit(Number(e.target.value))} style={{ marginLeft: 8, padding: '2px 4px', border: 'none', background: 'none', fontSize: 14, fontWeight: 600, color: '#333', cursor: 'pointer' }}>
          {PAGE_SIZE_OPTIONS.map((size) => <option key={size} value={size}>{size} per page</option>)}
        </select>
      )}
    />
  );
}
